<template>
    <!-- §5.3 asks for an el-drawer; Element here is 2.4.11 (Drawer arrived in
         2.8), so a right-aligned el-dialog plays the part, as phase 1 did. -->
    <el-dialog
        :visible="visible"
        width="820px"
        top="0"
        append-to-body
        custom-class="aff-drawer"
        @update:visible="(v) => $emit('update:visible', v)"
        @open="load"
    >
        <template slot="title">
            <div v-if="row" class="d-flex flex-wrap align-items-center" style="gap: 8px;">
                <span class="h3 mb-0 aff-mono">{{ row.code }}</span>
                <span>{{ row.member_deleted ? $t("marketing.affiliate.member.deleted_name") : row.name }}</span>
                <el-tag size="small" :type="statusTag">{{ tk("marketing.affiliate.member.status_", statusKey) }}</el-tag>
                <el-tag v-if="row.hold_payout" size="mini" type="danger">{{ $t("marketing.affiliate.member.hold") }}</el-tag>
            </div>
        </template>
        <div v-loading="loading" data-testid="aff-member-detail">
            <template v-if="row">
                <p v-if="row.suspend_reason" class="small text-danger mb-2">{{ $t("marketing.affiliate.member.suspend_reason") }}: {{ row.suspend_reason }}</p>
                <div class="row mb-2">
                    <div v-for="b in balances" :key="b.key" class="col-6 col-md-3 mb-2">
                        <div class="aff-stat">
                            <div class="small text-muted">{{ $t("marketing.affiliate.member." + b.key) }}</div>
                            <div class="h4 mb-0">{{ money(b.value) }}</div>
                        </div>
                    </div>
                </div>
                <p class="small text-muted mb-2">
                    {{ $t("marketing.affiliate.member.totals", { clicks: row.clicks_total || 0, orders: row.orders_total || 0 }) }} ·
                    {{ $t("marketing.affiliate.member.joined") }} {{ dateOnly(row.joined_at) }} · {{ row.phone_masked || "-" }}
                </p>

                <div class="aff-stat mb-3">
                    <affiliate-eligibility-panel :member-id="memberId" @changed="$emit('changed')"></affiliate-eligibility-panel>
                </div>

                <el-tabs v-model="tab" @tab-click="onTab">
                    <el-tab-pane :label="$t('marketing.affiliate.member.tab_commissions')" name="commissions">
                        <el-table v-loading="commLoading" :data="commissions" size="small" class="aff-table bon-table" :empty-text="$t('marketing.affiliate.empty')">
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.date')" width="135"><template v-slot="{ row: c }">{{ fmt(c.created_at || c.placed_at) }}</template></el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.order')" min-width="140">
                                <template v-slot="{ row: c }">{{ c.order_no || "-" }}<div class="small text-muted">{{ c.kind === "adjustment" ? $t("marketing.affiliate.commission.kind_adjustment") : c.product_name }}</div></template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.amount')" width="120" align="right"><template v-slot="{ row: c }">{{ money(c.amount) }}</template></el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.status')" width="140">
                                <template v-slot="{ row: c }">
                                    <el-tag size="mini" :type="commTag(c.status)">{{ tk("marketing.affiliate.commission.status_", c.status) }}</el-tag>
                                    <el-tag v-if="c.zero_reason" size="mini" type="danger" class="ml-1">{{ tk("marketing.affiliate.commission.zero_", c.zero_reason) }}</el-tag>
                                </template>
                            </el-table-column>
                        </el-table>
                        <el-pagination class="mt-2 text-right" layout="total, prev, pager, next" :total="commTotal" :page-size="20" :current-page="commPage" @current-change="loadCommissions"></el-pagination>
                        <router-link :to="{ path: '/marketing/affiliate/commissions', query: { code: row.code } }" class="small" @click.native="$emit('update:visible', false)">
                            {{ $t("marketing.affiliate.member.open_ledger") }} →
                        </router-link>
                    </el-tab-pane>

                    <el-tab-pane v-if="isFinance" :label="$t('marketing.affiliate.member.tab_profile')" name="profile">
                        <div v-loading="profileLoading" data-testid="aff-profile-tab">
                            <p v-if="profileMissing" class="text-muted">{{ $t("marketing.affiliate.profile.status_none") }}</p>
                            <template v-else-if="profile">
                                <div class="d-flex align-items-center mb-3" style="gap: 8px;">
                                    <el-tag :type="profileTag(profile.status)">{{ tk("marketing.affiliate.profile.status_", profile.status) }}</el-tag>
                                    <small v-if="profile.verified_at" class="text-muted">{{ profile.verified_by_name }} · {{ fmt(profile.verified_at) }}</small>
                                </div>
                                <el-alert v-if="profile.status === 'rejected' && profile.reject_reason" type="error" :closable="false" class="mb-3" :title="$t('marketing.affiliate.profile.reject_reason') + ': ' + profile.reject_reason"></el-alert>
                                <dl class="row aff-dl">
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.full_name") }}</dt><dd class="col-sm-8">{{ profile.full_name || "-" }}</dd>
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.national_id") }}</dt><dd class="col-sm-8 aff-mono">{{ profile.national_id || "-" }}</dd>
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.address") }}</dt><dd class="col-sm-8">{{ profile.address || "-" }} {{ profile.zipcode }}</dd>
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.bank") }}</dt><dd class="col-sm-8">{{ profile.bank_name_th || profile.bank_code || "-" }}</dd>
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.account_no") }}</dt><dd class="col-sm-8 aff-mono">{{ profile.bank_account_no || "-" }}</dd>
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.account_name") }}</dt><dd class="col-sm-8">{{ profile.bank_account_name || "-" }}</dd>
                                    <dt class="col-sm-4">{{ $t("marketing.affiliate.profile.pdpa") }}</dt><dd class="col-sm-8">{{ profile.pdpa_consent_at ? fmt(profile.pdpa_consent_at) + " (v" + profile.pdpa_consent_version + ")" : "-" }}</dd>
                                </dl>
                                <div class="d-flex flex-wrap mb-3" style="gap: 8px;">
                                    <base-button size="sm" type="secondary" :disabled="!profile.has_id_card" data-testid="aff-view-id" @click="openFile('id_card')">
                                        <i class="fa fa-id-card"></i> {{ $t("marketing.affiliate.profile.view_id_card") }}
                                    </base-button>
                                    <base-button size="sm" type="secondary" :disabled="!profile.has_bookbank" data-testid="aff-view-bookbank" @click="openFile('bookbank')">
                                        <i class="fa fa-book"></i> {{ $t("marketing.affiliate.profile.view_bookbank") }}
                                    </base-button>
                                    <small class="text-muted align-self-center">{{ $t("marketing.affiliate.profile.link_expiry") }}</small>
                                </div>
                                <div v-if="canWriteFin && profile.status === 'submitted'" class="d-flex" style="gap: 8px;">
                                    <base-button size="sm" type="success" :loading="acting" data-testid="aff-verify" @click="verify">{{ $t("marketing.affiliate.profile.verify") }}</base-button>
                                    <base-button size="sm" type="danger" :loading="acting" data-testid="aff-reject" @click="reject">{{ $t("marketing.affiliate.profile.reject") }}</base-button>
                                </div>
                            </template>
                        </div>
                    </el-tab-pane>

                    <el-tab-pane :label="$t('marketing.affiliate.member.tab_audit')" name="audit">
                        <el-table :data="row.audit || []" size="small" class="aff-table bon-table" :empty-text="$t('marketing.affiliate.empty')">
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.date')" width="140"><template v-slot="{ row: a }">{{ fmt(a.created_at) }}</template></el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.audit.action')" width="160">
                                <template v-slot="{ row: a }">{{ $te("marketing.affiliate.audit." + a.action) ? $t("marketing.affiliate.audit." + a.action) : a.action }}</template>
                            </el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.audit.by')" width="130"><template v-slot="{ row: a }">{{ a.actor_name || a.actor || "-" }}</template></el-table-column>
                            <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.audit.detail')" min-width="160"><template v-slot="{ row: a }"><small class="text-muted">{{ detailText(a.detail) }}</small></template></el-table-column>
                        </el-table>
                    </el-tab-pane>
                </el-tabs>
            </template>
        </div>
    </el-dialog>
</template>

<script>
import { Dialog, Tabs, TabPane, Table, TableColumn, Tag, Alert, Pagination, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliateService } from "@/resource";
import { apiErrorCode } from "@/utils/marketing";
import { commissionStatusTag, memberStatusKey, profileStatusTag } from "@/utils/affiliate";
import affiliateMixin from "./affiliateMixin";
import AffiliateEligibilityPanel from "./AffiliateEligibilityPanel.vue";

// Member detail "drawer" (Phase 3 spec 2026-10-13 §5.3): balances + tabs
// ค่าคอม / ข้อมูลรับเงิน (affiliate_finance only, unmasked, 5-minute file
// links) / ประวัติ (audit).
export default {
    name: "AffiliateMemberDetail",
    directives: { loading: Loading.directive },
    components: {
        [Dialog.name]: Dialog,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [Pagination.name]: Pagination,
        AffiliateEligibilityPanel
    },
    mixins: [affiliateMixin],
    props: {
        visible: { type: Boolean, default: false },
        memberId: { type: [Number, String], default: null }
    },
    data() {
        return {
            loading: false,
            row: null,
            tab: "commissions",
            commissions: [],
            commTotal: 0,
            commPage: 1,
            commLoading: false,
            profile: null,
            profileMissing: false,
            profileLoading: false,
            acting: false
        };
    },
    computed: {
        ...mapGetters({ isFinance: "Auth/isAccessAffiliateFinance", canWriteFin: "Auth/canWriteAffiliateFinance" }),
        statusKey() {
            return memberStatusKey(this.row);
        },
        statusTag() {
            return this.statusKey === "active" ? "success" : this.statusKey === "suspended" ? "danger" : "info";
        },
        balances() {
            const r = this.row || {};
            return [
                { key: "pending", value: r.pending },
                { key: "approved", value: r.approved_unpaid },
                { key: "in_payout", value: r.in_payout },
                { key: "paid_total", value: r.paid_gross_total }
            ];
        }
    },
    methods: {
        commTag: commissionStatusTag,
        profileTag: profileStatusTag,
        detailText(d) {
            if (!d || typeof d !== "object") return "";
            return Object.keys(d).map((k) => `${k}: ${typeof d[k] === "object" ? JSON.stringify(d[k]) : d[k]}`).join(" · ");
        },
        async load() {
            if (!this.memberId) return;
            this.tab = "commissions";
            this.row = null;
            this.profile = null;
            this.profileMissing = false;
            this.loading = true;
            try {
                this.row = this.unwrap(await AffiliateService.affiliate(this.memberId));
                this.loadCommissions(1);
            } catch (err) {
                this.showError(err);
            } finally {
                this.loading = false;
            }
        },
        async loadCommissions(page) {
            this.commPage = page || 1;
            this.commLoading = true;
            try {
                const res = await AffiliateService.commissions({ affiliate_member_id: this.memberId, page: this.commPage, limit: 20 });
                const b = res.data || {};
                this.commissions = b.data || [];
                this.commTotal = b.total || 0;
            } catch (err) {
                this.commissions = [];
                this.showError(err);
            } finally {
                this.commLoading = false;
            }
        },
        onTab(t) {
            if (t.name === "profile" && !this.profile && !this.profileMissing) this.loadProfile();
        },
        async loadProfile() {
            this.profileLoading = true;
            try {
                this.profile = this.unwrap(await AffiliateService.profile(this.memberId));
            } catch (err) {
                if (apiErrorCode(err) === "not_found" || (err.response && err.response.status === 404)) this.profileMissing = true;
                else this.showError(err);
            } finally {
                this.profileLoading = false;
            }
        },
        // 5-minute presigned link (each request audited server-side), new tab.
        async openFile(type) {
            const w = window.open("", "_blank");
            if (w) w.opener = null;
            try {
                const d = this.unwrap(await AffiliateService.fileUrl(this.memberId, type));
                if (w && d && d.url) w.location.href = d.url;
                else if (w) w.close();
            } catch (err) {
                if (w) w.close();
                this.showError(err);
            }
        },
        async verify() {
            if (!(await this.confirmBox(this.$t("marketing.affiliate.profile.confirm_verify", { name: this.profile.full_name }), { icon: "question" }))) return;
            this.acting = true;
            try {
                this.profile = this.unwrap(await AffiliateService.verify(this.memberId, { expected_updated_at: this.profile.updated_at || null }));
                this.notifyOk();
                this.$emit("changed");
            } catch (err) {
                this.showError(err);
                if (apiErrorCode(err) === "stale" || apiErrorCode(err) === "not_submitted") this.loadProfile();
            } finally {
                this.acting = false;
            }
        },
        async reject() {
            const reason = await this.askReason(this.$t("marketing.affiliate.profile.reject"), { label: this.$t("marketing.affiliate.profile.reject_reason"), danger: true });
            if (reason === null) return;
            this.acting = true;
            try {
                this.profile = this.unwrap(await AffiliateService.reject(this.memberId, { reason, expected_updated_at: this.profile.updated_at || null }));
                this.notifyOk();
                this.$emit("changed");
            } catch (err) {
                this.showError(err);
                if (apiErrorCode(err) === "stale" || apiErrorCode(err) === "not_submitted") this.loadProfile();
            } finally {
                this.acting = false;
            }
        }
    }
};
</script>

<style>
.el-dialog.aff-drawer {
    margin: 0 0 0 auto !important;
    max-width: 100vw;
    min-height: 100vh;
    border-radius: 0;
}
</style>
<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}

.aff-mono {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.04em;
}
.aff-stat {
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 8px 12px;
}
.aff-dl dt {
    font-weight: 600;
}
</style>
